import { useState } from 'react'
import { View, Text } from 'react-native'
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { CheckCircle2 } from '@/components/ui/icons'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { TextInput } from '@/components/ui/TextInput'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { ToursStars } from '@/components/tours/ToursStars'
import { useSubmitTourReview } from '@/lib/hooks/use-tours'
import { toursPaymentErrorKey } from '@/lib/tours/payment-errors'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors } from '@/lib/theme/tokens'
import { FONTS, SPACING } from '@/lib/brand'

export default function ToursReviewScreen() {
  const params = useLocalSearchParams<{ bookingId?: string | string[] }>()
  const bookingId = Array.isArray(params.bookingId) ? params.bookingId[0] : params.bookingId
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const submitReview = useSubmitTourReview()

  const [rating, setRating] = useState(0)
  const [comment, setComment] = useState('')
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  if (!bookingId) return <Redirect href="/(tabs)/tours/bookings" />

  if (done) {
    return (
      <ScreenContainer variant="content">
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            paddingVertical: SPACING['3xl'],
          }}
        >
          <CheckCircle2 size={56} color={colors.success} strokeWidth={1.6} />
          <Text
            style={{
              marginTop: SPACING.lg,
              fontFamily: FONTS.bodyBold,
              fontSize: 22,
              color: colors.textPrimary,
            }}
            testID="tours.review-thanks"
          >
            {t('tours.reviews.thanks')}
          </Text>
          <Button
            title={t('tours.bookings.title')}
            size="lg"
            onPress={() => router.replace('/(tabs)/tours/bookings')}
            style={{ marginTop: SPACING.xl, minWidth: 240 }}
          />
        </View>
      </ScreenContainer>
    )
  }

  const submit = async () => {
    setErrorKey(null)
    if (rating < 1) {
      setErrorKey('tours.errors.bad_request')
      return
    }
    try {
      await submitReview.mutateAsync({ bookingId, rating, comment: comment.trim() || undefined })
      setDone(true)
    } catch (err) {
      setErrorKey(toursPaymentErrorKey((err as { code?: string }).code))
    }
  }

  return (
    <ScreenContainer variant="content" scroll={false}>
      <KeyboardAwareView
        includeTopInset={false}
        contentContainerStyle={{ paddingHorizontal: SPACING.lg, paddingBottom: SPACING['2xl'] }}
        showsVerticalScrollIndicator={false}
      >
        <ScreenHeader
          title={t('tours.reviews.write')}
          backTestID="tours.review-back"
          onBackPress={() => router.back()}
          style={{ paddingHorizontal: 0 }}
        />

        <View style={{ marginTop: SPACING.lg }}>
          <Card>
            <Text style={{ fontFamily: FONTS.body, fontSize: 15, color: colors.textSecondary }}>
              {t('tours.reviews.your_rating')}
            </Text>
            <View style={{ marginTop: SPACING.sm }}>
              <ToursStars value={rating} size={32} onChange={setRating} />
            </View>
            <View style={{ marginTop: SPACING.md }}>
              <TextInput
                label={t('tours.reviews.comment_label')}
                value={comment}
                onChangeText={setComment}
                placeholder={t('tours.reviews.comment_placeholder')}
                multiline
                testID="tours.review-comment"
              />
            </View>
          </Card>
        </View>

        {errorKey ? (
          <Text
            style={{
              marginTop: SPACING.md,
              fontFamily: FONTS.body,
              fontSize: 14,
              color: colors.textError,
            }}
            testID="tours.review-error"
          >
            {t(errorKey)}
          </Text>
        ) : null}

        <Button
          title={t('tours.reviews.submit')}
          size="lg"
          loading={submitReview.isPending}
          disabled={rating < 1}
          onPress={submit}
          style={{ marginTop: SPACING.lg }}
          testID="tours.review-submit"
        />
      </KeyboardAwareView>
    </ScreenContainer>
  )
}
